import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { actionGetComments } from '@lib/core/comments/slices/comments';
import { useProductFeedback } from '@lib/core/products/hooks/useProductFeedback';
import { useRetailerLocation } from '@lib/core/retailers/hooks/useRetailerLocation';
import { BrowserUrlConsts, isApplicationKiosk } from '@lib/core/service/consts';
import { useApp } from '@lib/core/service/hooks';
import { setFidelityCardIdFromKiosk, setIsTasteIdGuideShown } from '@lib/core/service/slices';
import { isAppInIframe } from '@lib/core/service/utils';
import { actionGetServiceTermsList } from '@lib/core/serviceTerms/slices';
import { useLocationLog, useUser } from '@lib/core/users/hooks';
import { useFidelityCard } from '@lib/core/users/hooks/useFidelityCard';
import { useUserKiosk } from '@lib/core/users/hooks/useUserKiosk';
import { selectUserRefreshToken } from '@lib/core/users/selectors/auth';
import { actionGetUserCharacters } from '@lib/core/users/slices/characters';
import { actionGetDietaryPreferenceListData } from '@lib/core/users/slices/dietaryPreference';
import { actionGetLocationLog, actionUpdateLocationLog } from '@lib/core/users/slices/locationLog';
import { actionGetProductList } from '@lib/core/users/slices/productList';
import { actionGetProductRateData } from '@lib/core/users/slices/productRate';
import { actionGetProfileData } from '@lib/core/users/slices/profile';
import { actionGenerateUserSession, actionGetUserData } from '@lib/core/users/slices/user';
import { syncAuthToHost } from '@lib/tools/comms/utils';
import { PRODUCT_LIST_WISHLIST } from '@lib/tools/shared/helpers/consts';
import { resetTastePathData } from '@lib/tools/tastePathProducts/slices';
import { useAddons, useEffectSkipFirst } from '@lib/tools/views/hooks';
import { useTypedDispatch } from '@lib/tools/views/hooks/useTypedDispatch';

const UserProvider = ({ children }) => {
  const dispatch = useTypedDispatch();
  const [searchParams] = useSearchParams();

  const { authToken, handleResetUserData, isUserAuthenticated, profileId, userSessionId } = useUser();
  const refreshToken = useSelector(selectUserRefreshToken);
  const { isUserKioskAdmin } = useUserKiosk();
  const { getAllSubmittedProductFeedbacks } = useProductFeedback();

  const { locale, fidelityCardIdFromKiosk } = useApp();
  const { isDisableLocationTrackAddon, isInteractiveTastingAddon } = useAddons();
  const { retailerLocationId, retailerLocationSlug } = useRetailerLocation();
  const { isLocationLogDataLoaded, isLocationLogLoading } = useLocationLog();
  const { fidelityCardId } = useFidelityCard();

  const hideTasteIdGuide = () => dispatch(setIsTasteIdGuideShown(true));

  // this function is used to fetch the user related data
  const fetchUserRelatedData = () => {
    /** Fetch user data */
    dispatch(actionGetUserData());

    /** Fetch user characters */
    dispatch(actionGetUserCharacters());

    /** Fetch profile data */
    dispatch(actionGetProfileData());

    /** Fetch user locationLog */
    dispatch(actionGetLocationLog());

    /** Fetch user dietary preference data */
    dispatch(actionGetDietaryPreferenceListData());

    if (!isApplicationKiosk) {
      dispatch(actionGetServiceTermsList());
    }
  };

  // this function is used to fetch the x-profile user related data
  const fetchProfileRelatedData = () => {
    if (!isApplicationKiosk) {
      dispatch(actionGetProductList({ listName: PRODUCT_LIST_WISHLIST }))
        .unwrap()
        .then(res => res?.listData?.length > 0 && hideTasteIdGuide());

      /** We use Comments feature for:
       *  Widget - for all users */
      dispatch(actionGetComments());

      if (isInteractiveTastingAddon) {
        /** Fetch user product feedbacks */
        getAllSubmittedProductFeedbacks();
      }
    }
  };

  /** this useEffect is used to load data in Kiosk after scanning fidelity card */
  useEffect(() => {
    if (isApplicationKiosk && fidelityCardId && retailerLocationId && locale) {
      dispatch(actionGetProductList({ listName: PRODUCT_LIST_WISHLIST }));
      dispatch(actionGetProfileData());
    }
  }, [retailerLocationId, locale, fidelityCardId]);

  // * this use Effect runs only once after the first render of the component
  // * it is used to fetch the user and all the related data connected to the profile id
  useEffect(() => {
    if (!isApplicationKiosk) {
      if (isUserAuthenticated) {
        fetchUserRelatedData();
      }

      // * this is needed for promo qr code in the catalog page
      // * to show the modal only if we have the fidelity card id
      // * because we can't set the fidelity card for now because of mixpanel management
      const fidelityCardIdFromUrl = searchParams.get(BrowserUrlConsts.fidelityId);
      if (fidelityCardIdFromUrl && !fidelityCardIdFromKiosk) {
        dispatch(setFidelityCardIdFromKiosk(fidelityCardIdFromUrl));
      }
    }

    if (!userSessionId) {
      dispatch(actionGenerateUserSession());
    }
  }, []);

  useEffect(() => {
    fetchProfileRelatedData();
  }, [isUserAuthenticated]);

  useEffect(() => {
    if (!isApplicationKiosk) {
      /** We use Feedback feature for:
       *  Taste Path application - for all users.
       *  Widget and App applications - for all users */
      dispatch(actionGetProductRateData())
        .unwrap()
        .then(res => res?.length && hideTasteIdGuide());
    }
  }, [isApplicationKiosk, isUserAuthenticated]);

  // * this use Effect runs only once after the first render of the component
  // * and skip the first render to avoid to make conflict with above useEffect
  // * it is used to associate character with user's account once he make a login or create an account
  useEffectSkipFirst(() => {
    if (isUserAuthenticated && !isApplicationKiosk) {
      fetchUserRelatedData();
      dispatch(resetTastePathData());
    } else {
      handleResetUserData();
    }
  }, [isUserAuthenticated]);

  useEffectSkipFirst(() => {
    dispatch(actionGenerateUserSession());
  }, [fidelityCardId]);

  useEffect(() => {
    if (isUserAuthenticated) {
      dispatch(resetTastePathData());

      const isNonKioskUserOverKioskEnv = isApplicationKiosk && !isUserKioskAdmin;
      const isKioskUserOverNonKioskEnv = !isApplicationKiosk && isUserKioskAdmin;
      if ((isUserAuthenticated && isNonKioskUserOverKioskEnv) || isKioskUserOverNonKioskEnv) {
        handleResetUserData();
      }
    }
  }, [isUserAuthenticated]);

  useEffect(() => {
    if (
      isUserAuthenticated &&
      !isAppInIframe &&
      !isDisableLocationTrackAddon &&
      retailerLocationSlug &&
      isLocationLogDataLoaded &&
      !isLocationLogLoading
    ) {
      dispatch(actionUpdateLocationLog(retailerLocationSlug));
    }
  }, [isUserAuthenticated, isAppInIframe, isDisableLocationTrackAddon, isLocationLogDataLoaded, retailerLocationSlug]);

  useEffect(() => {
    syncAuthToHost({
      accessToken: authToken,
      profileId,
      refreshToken,
    });
  }, [authToken, refreshToken, profileId]);

  return children;
};

export default UserProvider;
